import { useCallback, useEffect, useMemo, useState } from 'react';
import API_BASE_URL from '../config/api';
import { apiClient } from '../services/apiClient';
import { ADDITIVES_UNIT, withBusinessUnit } from '../contexts/BusinessUnitContext';
import { hasPermission } from '../utils/permissions';

const PAINT_PROSPECTING_HREF = '/pinturas/commercial/prospecting';
const ADDITIVES_PROSPECTING_HREF = '/aditivos/commercial/prospecting';

const toYmd = (value) => {
  if (!value) return '';
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const addDaysYmd = (ymd, days) => {
  const d = new Date(`${ymd}T00:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  d.setDate(d.getDate() + days);
  return toYmd(d);
};

const todayYmd = () => toYmd(new Date());

const normalizeName = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

/**
 * Datos derivados para el sidebar derecho: notificaciones y agenda hoy/mañana.
 */
export default function useRightSidebarData({ enabled = false, user = null } = {}) {
  const [trackingRows, setTrackingRows] = useState([]);
  const [calendarRows, setCalendarRows] = useState([]);
  const [tripRows, setTripRows] = useState([]);
  const [personalRows, setPersonalRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const canSeeAdditives = hasPermission(user, 'additives.commercial.read');

  const load = useCallback(async () => {
    if (!enabled) return;
    setIsLoading(true);
    setError('');
    try {
      const trackingUrl = `${API_BASE_URL}/api/contacts/activities/tracking`;
      const results = await Promise.allSettled([
        apiClient(trackingUrl),
        apiClient(`${API_BASE_URL}/api/quotations/logistics/calendar`),
        apiClient(`${API_BASE_URL}/api/quotations/logistics/trips`),
        canSeeAdditives ? apiClient(withBusinessUnit(trackingUrl, ADDITIVES_UNIT)) : Promise.resolve([]),
        apiClient(`${API_BASE_URL}/api/agenda/items?include_done=false`),
      ]);
      const listOf = (result) =>
        result.status === 'fulfilled' && Array.isArray(result.value) ? result.value : [];
      setTrackingRows([
        ...listOf(results[0]).map((row) => ({ ...row, prospectingHref: PAINT_PROSPECTING_HREF })),
        ...listOf(results[3]).map((row) => ({ ...row, prospectingHref: ADDITIVES_PROSPECTING_HREF })),
      ]);
      setCalendarRows(listOf(results[1]));
      setTripRows(listOf(results[2]));
      setPersonalRows(listOf(results[4]));
      if (
        results.slice(0, 3).every((result) => result.status === 'rejected') &&
        !listOf(results[3]).length &&
        results[4].status === 'rejected'
      ) {
        setError('No se pudieron cargar avisos ni agenda.');
      }
    } catch (err) {
      setTrackingRows([]);
      setCalendarRows([]);
      setTripRows([]);
      setPersonalRows([]);
      setError(err?.message || 'Error al cargar el sidebar.');
    } finally {
      setIsLoading(false);
    }
  }, [enabled, canSeeAdditives]);

  useEffect(() => {
    load();
  }, [load]);

  const userNameKey = normalizeName(user?.full_name);

  const myTracking = useMemo(() => {
    if (!userNameKey) return trackingRows;
    const mine = trackingRows.filter((row) => normalizeName(row.owner_name) === userNameKey);
    return mine.length ? mine : trackingRows;
  }, [trackingRows, userNameKey]);

  const today = todayYmd();
  const tomorrow = addDaysYmd(today, 1);

  const notifications = useMemo(() => {
    const items = [];

    myTracking.forEach((row) => {
      const due = toYmd(row.next_step_date);
      const isOverdue = String(row.due_state || '').toLowerCase() === 'vencida' || (due && due < today);
      const isSoon = due === today || due === tomorrow;
      if (!isOverdue && !isSoon) return;
      items.push({
        id: `crm-${row.id}`,
        kind: 'crm',
        severity: isOverdue ? 'overdue' : 'soon',
        title: row.next_step || row.action || 'Próximo paso',
        subtitle: [row.contact_name, row.organization_name].filter(Boolean).join(' · ') || 'CRM',
        date: due,
        href: row.prospectingHref,
      });
    });

    calendarRows.forEach((row) => {
      const due = toYmd(row.scheduled_date);
      if (due !== today && due !== tomorrow && !(due && due < today)) return;
      const isOverdue = due && due < today;
      items.push({
        id: `dlv-${row.id}`,
        kind: 'logistics',
        severity: isOverdue ? 'overdue' : 'soon',
        title: `Entrega ${row.sale_code || row.quotation_code || ''}`.trim(),
        subtitle: [row.organization_name, row.delivery_status].filter(Boolean).join(' · ') || 'Despacho',
        date: due,
        href: '/pinturas/commercial/logistics',
      });
    });

    personalRows.forEach((row) => {
      const due = toYmd(row.scheduled_date);
      const isOverdue = due && due < today;
      const isSoon = due === today || due === tomorrow;
      if (!isOverdue && !isSoon) return;
      const kindLabel =
        row.kind === 'meeting'
          ? 'Reunión'
          : row.kind === 'sprint'
            ? 'Sprint'
            : row.kind === 'todo'
              ? 'To-do'
              : 'Actividad';
      items.push({
        id: `agenda-${row.id}`,
        kind: 'personal',
        severity: isOverdue ? 'overdue' : 'soon',
        title: row.title || kindLabel,
        subtitle: [kindLabel, row.sprint_name, row.status].filter(Boolean).join(' · ') || 'Mi agenda',
        date: due,
        href: `/agenda?tab=${row.kind || 'calendar'}`,
      });
    });

    items.sort((a, b) => {
      if (a.severity !== b.severity) return a.severity === 'overdue' ? -1 : 1;
      return String(a.date || '').localeCompare(String(b.date || ''));
    });
    return items;
  }, [myTracking, calendarRows, personalRows, today, tomorrow]);

  const buildAgendaDay = useCallback(
    (ymd) => {
      const items = [];

      myTracking.forEach((row) => {
        if (toYmd(row.next_step_date) !== ymd) return;
        items.push({
          id: `crm-${row.id}-${ymd}`,
          kind: 'crm',
          title: row.next_step || row.action || 'Actividad CRM',
          subtitle: [row.contact_name, row.organization_name].filter(Boolean).join(' · '),
          href: row.prospectingHref,
        });
      });

      calendarRows.forEach((row) => {
        if (toYmd(row.scheduled_date) !== ymd) return;
        items.push({
          id: `dlv-${row.id}-${ymd}`,
          kind: 'logistics',
          title: `Entrega ${row.sale_code || ''}`.trim() || 'Entrega',
          subtitle: [row.organization_name, row.trip_code].filter(Boolean).join(' · '),
          href: '/pinturas/commercial/logistics',
        });
      });

      tripRows.forEach((row) => {
        if (toYmd(row.trip_date) !== ymd) return;
        items.push({
          id: `trip-${row.id}-${ymd}`,
          kind: 'trip',
          title: `Viaje ${row.trip_code || ''}`.trim() || 'Viaje',
          subtitle: [row.provider_name || row.carrier_name, row.destination_summary]
            .filter(Boolean)
            .join(' · '),
          href: '/pinturas/commercial/logistics',
        });
      });

      personalRows.forEach((row) => {
        if (toYmd(row.scheduled_date) !== ymd) return;
        const kindLabel =
          row.kind === 'meeting'
            ? 'Reunión'
            : row.kind === 'sprint'
              ? 'Sprint'
              : row.kind === 'todo'
                ? 'To-do'
                : 'Actividad';
        const timeRange = [row.start_time, row.end_time].filter(Boolean).join('–');
        items.push({
          id: `agenda-${row.id}-${ymd}`,
          kind: 'personal',
          title: row.title || kindLabel,
          subtitle: [kindLabel, timeRange, row.sprint_name].filter(Boolean).join(' · '),
          href: `/agenda?tab=${row.kind || 'calendar'}`,
        });
      });

      return items;
    },
    [myTracking, calendarRows, tripRows, personalRows]
  );

  const agenda = useMemo(
    () => ({
      today: buildAgendaDay(today),
      tomorrow: buildAgendaDay(tomorrow),
      todayLabel: today,
      tomorrowLabel: tomorrow,
    }),
    [buildAgendaDay, today, tomorrow]
  );

  return {
    isLoading,
    error,
    notifications,
    agenda,
    reload: load,
  };
}
